/**
 * Copyright 2026 ForgeRock AS. All Rights Reserved
 *
 * Use of this code requires a commercial software license with ForgeRock AS
 * or with one of its affiliates. All use shall be exclusively subject
 * to such license between the licensee and ForgeRock AS.
 */

import { useRouter } from 'vue-router';
import { useUserStore } from '@forgerock/platform-shared/src/stores/user';
import {
  getAccessToken, login, logout,
} from '@/api/IdmAuthenticationApi';

/**
 * Composable function to handle authentication
 * @returns {Object} The authentication functions
 */
// eslint-disable-next-line import/prefer-default-export
export function useAuth() {
  let userStoreInstance;
  const router = useRouter();

  /**
   * Returns a singleton instance of the user store.
   * If the instance does not exist, it initializes it using `useUserStore`.
   *
   * @returns {UserStore} The user store instance.
   */
  function getUserStore() {
    if (!userStoreInstance) {
      userStoreInstance = useUserStore();
    }
    return userStoreInstance;
  }

  /**
   * Check if the user is authenticated
   * @returns {Boolean} True if the user is authenticated
   */
  function isAuthenticated() {
    const userStore = getUserStore();
    return !!userStore.userId;
  }

  /**
   * Restores the admin session by fetching the access token and updating the user store.
   * Sets the user ID, assigns the IDM UI admin roles, and updates IDM roles from the authorization data.
   *
   * @async
   * @returns {Promise<void>} Resolves when the admin session is restored.
   */
  async function restoreAdminSession() {
    const { data } = await getAccessToken();
    const userStore = getUserStore();
    userStore.userId = data.authorization.id;
    userStore.idmUIAdminRoles = ['internal/role/openidm-admin'];
    userStore.idmRoles = data.authorization.roles || [];
  }

  /**
   * Logs in an IDM admin user using the provided credentials.
   * Updates the user store with user ID and roles, and redirects to the Dashboard.
   *
   * @async
   * @param {string} username - The username of the IDM admin.
   * @param {string} password - The password of the IDM admin.
   * @returns {Promise<void>} Resolves when login and redirection are complete.
   */
  async function loginIdmAdmin(username, password) {
    const { data } = await login(username, password, false);
    const userStore = getUserStore();
    userStore.userId = data.authorization.id;
    userStore.idmUIAdminRoles = ['internal/role/openidm-admin'];
    userStore.idmRoles = data.authorization.roles || [];
    router.push({ name: 'Dashboard' });
  }

  // trigger logout from anywhere in the SPA by calling this global function
  function initializeLogout() {
    window.logout = async () => {
      await logout();

      // reset stores to default data
      const userStore = getUserStore();
      userStore.$reset();

      window.location.hash = '/login';
    };
  }

  return {
    isAuthenticated,
    restoreAdminSession,
    loginIdmAdmin,
    initializeLogout,
  };
}
